import { EventContextProvider, useAnalytics } from "@codat/analytics";
import {
  CheckIcon,
  Tooltip,
  TooltipContent,
  Typography,
  useTheme,
} from "@codat/orchard-ui";
import { Integration } from "api/company";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";

import { ApiLink } from "../../api/shared";
import { ContactSupport } from "../ContactSupport/ContactSupport";
import styles from "./IntegrationCard.module.css";

export interface IntegrationCardProps {
  integration: Integration;
  active: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  selectedIntegration?: string;
  eventProperties?: object;
  disabled?: boolean;
  link?: ApiLink["link"];
  clientName: string;
}

const HIDE_TOOLTIP_DELAY_MS = 250;

export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  active,
  onClick,
  selectedIntegration,
  eventProperties,
  disabled,
  link,
  clientName,
  ...props
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);
  const { colors } = useTheme();
  const analytics = useAnalytics();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    analytics.sendEvent("Integration card - Clicked", eventProperties);

    if (onClick) {
      onClick(e);
    }
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  });

  const button = (
    <button
      className={classNames(styles.card, {
        [styles.active]: active,
        [styles.inactive]:
          selectedIntegration && selectedIntegration !== integration.key,
        [styles.disabled]: disabled,
      })}
      onClick={!disabled ? handleClick : undefined}
      onMouseEnter={() => setShowTooltip(true)}
      onFocus={() => setShowTooltip(true)}
      onMouseLeave={() => {
        timeoutRef.current = window.setTimeout(
          () => setShowTooltip(false),
          HIDE_TOOLTIP_DELAY_MS
        );
      }}
      aria-disabled={disabled} // Can't use disabled attribute as mouse events won't work
      ref={buttonRef}
      {...props}
    >
      <img
        src={integration.logo?.square?.image?.src || integration.logoUrl}
        alt={integration.name}
      />{" "}
      <Typography variant="body_md" as="span">
        {integration.name}
      </Typography>
      {active && (
        <CheckIcon
          data-testid="integration-card-check-icon"
          fillColor={colors["color-ui-action"]}
        />
      )}
    </button>
  );

  return (
    <>
      {disabled ? (
        <EventContextProvider name="Disabled integration card">
          <Tooltip
            className={styles.disabledTooltip}
            id={`integration-disabled-tooltip-${integration.key}`}
            isOpen={showTooltip}
            placement="bottom"
            anchorElement={buttonRef.current ?? undefined}
            content={
              <TooltipContent
                title={
                  <>
                    <Typography
                      className={styles.disabledTooltipTitle}
                      variant="h2"
                      as="h2"
                    >
                      This software is disabled
                    </Typography>
                    <ContactSupport
                      clientName={clientName}
                      link={link}
                      onFocus={() => clearTimeout(timeoutRef.current)}
                      onBlur={() => setShowTooltip(false)}
                    />
                  </>
                }
              />
            }
          >
            {button}
          </Tooltip>
        </EventContextProvider>
      ) : (
        button
      )}
    </>
  );
};
